/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Profile and request navigation handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsAppointmentHandlers
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { prepareAppointmentRequestNavigation } from "../../logic/appointments/openAppointmentRequest.js";
import { loadAppointmentSenderCompany } from "../../logic/appointments/loadAppointmentSenderCompany.js";
import useViewAppointmentsRescheduleHandler from "./useViewAppointmentsRescheduleHandler.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @returns {Object} Appointment navigation handlers
 */
const useViewAppointmentsAppointmentHandlers = (ctx, fetching) => {
  const {
    navigate,
    eventData,
    appointments,
    participatingCompanyData,
    setActiveDropdownId,
    setIsLoading,
    setParticipatingCompanyData,
    setAppointments,
    setFilteredRequests,
  } = ctx;

  const { handleAppointmentReschedule } =
    useViewAppointmentsRescheduleHandler(ctx, fetching);

  const handleSeeProfile = useCallback(
    async (request) => {
      setActiveDropdownId(null);

      if (!request?._id || !eventData?._id) {
        return;
      }

      setIsLoading(true);
      try {
        const companyData = await loadAppointmentSenderCompany({
          navigate,
          appointmentId: request._id,
          eventId: eventData._id,
        });

        if (companyData) {
          navigate("/companyProfile", {
            state: {
              company: companyData,
              context: "appointments",
              event: eventData,
            },
          });
        } else {
          logger.error("Company not found for appointment profile");
        }
      } catch (error) {
        logger.error("Error fetching company profile:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setActiveDropdownId, eventData, setIsLoading, navigate],
  );

  const handleSeeRequest = useCallback(
    async (request) => {
      setActiveDropdownId(null);

      if (!request?._id || !eventData?._id) {
        return;
      }

      setIsLoading(true);
      try {
        const preparedNavigation = await prepareAppointmentRequestNavigation({
          navigate,
          request,
          eventData,
          appointments,
          participatingCompanyData,
          setters: {
            setAppointments,
            setFilteredRequests,
          },
        });

        if (!preparedNavigation) {
          logger.error("Appointment detail could not be loaded");
          return;
        }

        if (preparedNavigation.participatingCompany) {
          setParticipatingCompanyData(preparedNavigation.participatingCompany);
        }

        navigate("/appointmentRequest", {
          state: preparedNavigation.navigationState,
        });
      } catch (error) {
        logger.error("Error opening appointment request:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      setActiveDropdownId,
      eventData,
      setIsLoading,
      navigate,
      appointments,
      participatingCompanyData,
      setAppointments,
      setFilteredRequests,
      setParticipatingCompanyData,
    ],
  );

  return { handleSeeProfile, handleSeeRequest, handleAppointmentReschedule };
};

export default useViewAppointmentsAppointmentHandlers;
